import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { CandidatePanel } from './CandidatePanel';

/**
 * The candidate panel — the right-hand drawer that holds everything a recruiter currently
 * switches to Lever for — and the verdict words its chip wears.
 *
 * Same shape as the notes and email providers: the chip is on cards in several tabs, so the
 * provider owns the one drawer and a card just renders <FeedbackChip>. Nothing is fetched until a
 * chip is clicked; the drawer is only mounted while it is open.
 *
 * The VERDICTS are different from everything else in the panel: they ride on the board payload
 * (`candidateVerdicts`, one map for the whole board, free because the build already read that
 * feedback) so a chip can show the last round's answer with no click and no request at all. Every
 * other thing the panel shows is fetched on open, which is what keeps the board's first load out
 * of this feature.
 *
 * The board view mounts it with `key={boardEmail}`, so switching boards drops an open target
 * rather than carrying one board's candidate into another's drawer.
 */
interface PanelTarget {
  opportunityId: string;
  candidateName: string;
}

interface CandidatePanelApi {
  openPanel: (target: PanelTarget) => void;
  /** The latest round's verdict words for this candidate, or [] when nothing is submitted. */
  verdictsFor: (opportunityId: string) => string[];
}

const CandidatePanelCtx = createContext<CandidatePanelApi | null>(null);

/** Null outside a provider — the chip then renders nothing rather than a dead button. */
export function useCandidatePanel(): CandidatePanelApi | null {
  return useContext(CandidatePanelCtx);
}

const NONE: string[] = [];

export function CandidatePanelProvider({
  boardEmail,
  verdicts,
  children,
}: {
  boardEmail: string;
  /** `candidateVerdicts` from the board payload. Absent on an older proxy — then no chip wears one. */
  verdicts?: Record<string, string[]>;
  children: ReactNode;
}) {
  const [target, setTarget] = useState<PanelTarget | null>(null);
  const openPanel = useCallback((t: PanelTarget) => setTarget(t), []);
  const close = useCallback(() => setTarget(null), []);
  const api = useMemo<CandidatePanelApi>(
    () => ({ openPanel, verdictsFor: (id) => verdicts?.[id] || NONE }),
    [openPanel, verdicts]
  );
  return (
    <CandidatePanelCtx.Provider value={api}>
      {children}
      {target && (
        <CandidatePanel
          boardEmail={boardEmail}
          opportunityId={target.opportunityId}
          candidateName={target.candidateName}
          onClose={close}
        />
      )}
    </CandidatePanelCtx.Provider>
  );
}
